import { z } from "zod";
import { StuecklisteSchema } from "./common.js";

/**
 * Globaler Artikel-Catalog (Baustoff-Zentrum-weit, Single Source of Truth).
 *
 * Liegt unter `.bidi-handel/catalog/artikel.json`. Wird Tenant-übergreifend
 * geteilt: jeder Tenant referenziert nur die ihn betreffenden Catalog-IDs in
 * seinem `sortiment.json` und kann pro Artikel optional Felder überschreiben
 * (siehe schemas/sortiment.ts).
 *
 * Schema bewusst tolerant + erweiterbar:
 *  - `fachdaten` ist ein offenes Key-Value-Bag für Material-Eigenschaften
 *    (lambda, brandklasse, dichte, dicke_mm, wlg, ...) — keine Zwangs-Felder,
 *    weil die je nach Warengruppe stark variieren.
 *  - `externalRefs` ist mehrkatalog-fähig (Heinze/sirAdos/STLB-Bau/GAEB)
 *    analog zum LV-Katalog in bidi-takeoff (siehe feedback_pin-match-pattern.md).
 *  - `lifecycle` macht Auslaufmodelle ohne Löschen handhabbar — Bestand bleibt
 *    referenzierbar.
 *
 * IDs sind ULIDs, Code ist global eindeutig (Index-Constraint im Loader).
 */

export const GlobalArtikelExternalRefsSchema = z
  .object({
    heinzeOz: z.string().optional(),
    siradosId: z.string().optional(),
    stlbLb: z.string().optional(),
    gaebStlb: z.string().optional(),
  })
  .partial()
  .default({});
export type GlobalArtikelExternalRefs = z.infer<
  typeof GlobalArtikelExternalRefsSchema
>;

export const GlobalArtikelLifecycleSchema = z.enum([
  "active",
  "discontinued",
]);
export type GlobalArtikelLifecycle = z.infer<
  typeof GlobalArtikelLifecycleSchema
>;

export const GlobalArtikelSchema = z.object({
  /** ULID, intern stabiler Schlüssel — wird in TenantArtikelRef referenziert. */
  id: z.string().min(1),
  /** Hersteller- oder Händler-Code, global eindeutig. Wird im LV-Match als
   *  Primär-Display verwendet (z.B. "ROH-KS-240"). */
  code: z.string().min(1),
  bezeichnung: z.string().min(1),
  kurzText: z.string().optional(),
  ean: z.string().optional(),
  vpe: z.string().optional(),
  /** Verkaufs-/Preiseinheit (z.B. „Sack", „Box", „kg"). `listenpreis` ist pro `einheit`. */
  einheit: z.string().optional(),
  /**
   * Physikalische Einheit, in der LV-/vor-LV-Positionen kommen (z.B. „kg", „St", „m2").
   * Zusammen mit `inhaltProVE` ermöglicht das die Gebinde→Basis-Umrechnung in der
   * Bepreisung (Schritt 2). Fehlt es, gilt der Artikel als „Preis pro `einheit`" und
   * die Position muss alias-gleich sein (Schritt-1-Verhalten). */
  basiseinheit: z.string().optional(),
  /** Basiseinheiten pro Verkaufseinheit (z.B. 25 = 25 kg/Sack, 50 = 50 St/Box). */
  inhaltProVE: z.number().positive().optional(),
  /** Warengruppe als Freitext-Schlüssel (Konditionsmatrix-Lookup + Pre-Filter). */
  warengruppe: z.string().optional(),
  herstellerName: z.string().optional(),
  herstellerArtikelnr: z.string().optional(),
  lieferantId: z.string().optional(),
  /** Synonyme/Suchbegriffe — werden im Match-Pre-Filter zusätzlich zur
   *  Bezeichnung in den Token-Index aufgenommen. */
  suchbegriffe: z.array(z.string()).default([]),
  /** Lieferanten-Listenpreis (EUR/Einheit). Optional — manche Stammdaten
   *  haben nur Konditionspreise. Tenant kann via TenantArtikelRef
   *  überschreiben. */
  listenpreis: z.number().nonnegative().optional(),
  /** Offenes Key-Value-Bag für Material-Eigenschaften. Beispiele:
   *  { brandklasse: "A1", lambda: 0.035, dicke_mm: 140, dichte_kg_m3: 80 }. */
  fachdaten: z.record(z.string(), z.unknown()).default({}),
  /** Montage-Stückliste (Set): Komponenten + Preis-Modus. Ist dieser Artikel
   *  der gewählte Artikel einer Position, expandiert die Match-Phase die
   *  Stückliste zur Schnellkalkulation. Optional — nur Set-/Paket-Artikel
   *  tragen sie. `null` im PATCH entfernt eine bestehende Stückliste. */
  stueckliste: StuecklisteSchema.nullish(),
  /** Refs in externe Stammkataloge (für späteren Auto-Match). */
  externalRefs: GlobalArtikelExternalRefsSchema,
  lifecycle: GlobalArtikelLifecycleSchema.default("active"),
  /** ISO-8601 — wird beim Create/Update gesetzt, hilft beim Diff/Audit. */
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type GlobalArtikel = z.infer<typeof GlobalArtikelSchema>;

export const GlobalArtikelListSchema = z.array(GlobalArtikelSchema);
export type GlobalArtikelList = z.infer<typeof GlobalArtikelListSchema>;

export const GlobalCatalogSchema = z.object({
  schemaVersion: z.literal(1).default(1),
  artikel: GlobalArtikelListSchema.default([]),
});
export type GlobalCatalog = z.infer<typeof GlobalCatalogSchema>;
